import { useState, type FormEvent } from 'react';
import { ApiError, errorText, type Meta } from '../api';
import { adminApi, type Identity } from './api';

/** „Passwort vergessen": Adresse eingeben, Link per Mail, neues Passwort setzen. */
export function ForgotPasswordPage({ meta, onBack }: { meta: Meta; onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminApi.requestPasswordReset(email);
      setSent(true);
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) setError('Zu viele Versuche. Bitte in einer Stunde noch einmal.');
      else setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page" style={{ maxWidth: 440, paddingTop: 96 }}>
      <div>
        <p className="label label--brand">{meta.productName}</p>
        <h1 style={{ fontSize: 28, marginTop: 8 }}>Passwort vergessen</h1>
      </div>
      {sent ? (
        <div className="card">
          <p>Wenn es zu dieser Adresse einen Zugang gibt, ist eine Mail mit einem Link unterwegs. Der Link gilt eine Stunde.</p>
          <button type="button" className="link-button" onClick={onBack}>
            Zurück zur Anmeldung
          </button>
        </div>
      ) : (
        <form className="card" onSubmit={submit}>
          <p className="muted small">Du bekommst einen Link per E-Mail, mit dem du ein neues Passwort setzen kannst.</p>
          <div className="field">
            <label htmlFor="reset-email">E-Mail-Adresse</label>
            <input id="reset-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          {error && <div className="error">{error}</div>}
          <button type="submit" className="button" disabled={busy}>
            {busy ? 'Einen Moment …' : 'Link schicken'}
          </button>
          <button type="button" className="link-button" onClick={onBack}>
            Zurück zur Anmeldung
          </button>
        </form>
      )}
    </main>
  );
}

export function ResetPasswordPage({ meta, token, onDone }: { meta: Meta; token: string; onDone: (identity: Identity) => void }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onDone(await adminApi.resetPassword(token, password));
      window.history.replaceState(null, '', '/admin');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page" style={{ maxWidth: 440, paddingTop: 96 }}>
      <div>
        <p className="label label--brand">{meta.productName}</p>
        <h1 style={{ fontSize: 28, marginTop: 8 }}>Neues Passwort</h1>
      </div>
      <form className="card" onSubmit={submit}>
        <div className="field">
          <label htmlFor="new-password">Neues Passwort</label>
          <input id="new-password" type="password" autoComplete="new-password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} />
          <span className="muted small">Mindestens 10 Zeichen.</span>
        </div>
        {error && (
          <div className="error">
            {error} <a href="/admin">Zur Anmeldung</a>
          </div>
        )}
        <button type="submit" className="button" disabled={busy}>
          {busy ? 'Einen Moment …' : 'Passwort setzen und anmelden'}
        </button>
      </form>
    </main>
  );
}
